import Navigation from "@/components/layout/Navigation";
import Footer from "@/components/layout/Footer";
import NewsletterSubscription from "@/components/layout/NewsletterSubscription";
import ScrollReveal from "@/components/ScrollReveal";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Link } from "react-router-dom";

const About = () => {
  const journeySteps = [
    {
      number: "1",
      title: "The Problem",
      subtitle: "Frustrated with the Agency Model",
      description: "After spending years watching founder-led companies pour money into agencies with little to show for it long-term, we realized the fundamental flaw: you were building someone else's business, not your own."
    },
    {
      number: "2", 
      title: "The Insight",
      subtitle: "Content is a Capability, Not a Campaign",
      description: "The most successful companies we studied had one thing in common: they built internal content capabilities that scaled with their business, not external dependencies that capped their growth."
    },
    {
      number: "3",
      title: "The Solution", 
      subtitle: "Contencraft is Born",
      description: "We developed a systematic approach to transfer world-class content capabilities directly to founder-led companies, ensuring they own their growth engine rather than rent it."
    }
  ];

  const philosophyPrinciples = [
    {
      title: "Ownership Over Dependency",
      description: "We believe businesses thrive when they own their capabilities, not rent them. Every system we build becomes a permanent asset that grows in value.",
      icon: "🏆"
    },
    {
      title: "Founder-Led Authenticity", 
      description: "The best content comes from founders who deeply understand their market. We amplify that authentic voice, never replace it.",
      icon: "🎯"
    },
    {
      title: "Systems Over Services",
      description: "We don't just create content—we build the entire operating system that makes world-class content production scalable and sustainable.",
      icon: "⚡"
    }
  ];

  return (
    <div className="min-h-screen bg-background text-foreground">
      <Navigation />
      
      <main>
        {/* Hero Section */}
        <section className="relative section-spacing overflow-hidden">
          <div className="absolute inset-0 bg-gradient-to-br from-background via-background to-primary/10" />
          <div className="absolute inset-0">
            <div className="absolute top-20 left-20 w-72 h-72 bg-primary/20 rounded-full blur-3xl float-element" />
            <div className="absolute bottom-20 right-20 w-96 h-96 bg-accent/15 rounded-full blur-3xl float-element" />
          </div>
          
          <div className="container relative z-10 px-4 lg:px-8">
            <div className="max-w-4xl mx-auto text-center">
              <ScrollReveal>
                <h1 className="text-4xl sm:text-5xl lg:text-7xl font-display font-black mb-6 lg:mb-8">
                  <span className="block text-foreground mb-4">We Believe</span>
                  <span className="block bg-gradient-brand bg-clip-text text-transparent">
                    Ownership Beats Renting
                  </span>
                </h1>
              </ScrollReveal>
              
              <ScrollReveal delay={200}>
                <p className="text-xl lg:text-2xl text-muted-foreground max-w-3xl mx-auto leading-relaxed">
                  Contencraft was born from a simple realization: the most successful companies don't outsource their core capabilities—they build them internally.
                </p>
              </ScrollReveal>
            </div>
          </div>
        </section>

        {/* Journey Section */}
        <section className="section-spacing bg-gradient-to-b from-background to-card/50">
          <div className="container px-4 lg:px-8">
            <ScrollReveal>
              <div className="text-center mb-16">
                <h2 className="text-3xl lg:text-5xl font-display font-bold mb-6 bg-gradient-brand bg-clip-text text-transparent">
                  Our Journey
                </h2>
              </div>
            </ScrollReveal>

            <div className="max-w-6xl mx-auto">
              <div className="space-y-12 lg:space-y-20">
                {journeySteps.map((step, index) => (
                  <ScrollReveal key={step.number} delay={index * 200}>
                    <div className="grid lg:grid-cols-12 gap-8 lg:gap-12 items-center">
                      <div className={`lg:col-span-5 ${index % 2 === 1 ? 'lg:order-2' : ''}`}>
                        <div className="glass-panel p-8 lg:p-10 rounded-2xl">
                          <div className="flex items-center gap-4 mb-6">
                            <div className="w-12 h-12 rounded-full bg-gradient-primary flex items-center justify-center">
                              <span className="text-white font-bold text-xl">{step.number}</span>
                            </div>
                            <h3 className="text-2xl lg:text-3xl font-bold text-primary">{step.title}</h3>
                          </div>
                          <h4 className="text-xl lg:text-2xl font-semibold text-foreground mb-4">
                            {step.subtitle}
                          </h4>
                          <p className="text-lg text-muted-foreground leading-relaxed">
                            {step.description}
                          </p>
                        </div>
                      </div>
                      
                      <div className={`lg:col-span-7 ${index % 2 === 1 ? 'lg:order-1' : ''}`}>
                        <div className="relative">
                          <div className="w-full h-64 lg:h-80 bg-gradient-to-br from-primary/20 to-accent/20 rounded-2xl flex items-center justify-center">
                            <div className="text-6xl lg:text-8xl opacity-20">{step.number}</div>
                          </div>
                          <div className="absolute -inset-4 bg-gradient-primary opacity-20 rounded-3xl blur-2xl -z-10" />
                        </div>
                      </div>
                    </div>
                  </ScrollReveal>
                ))}
              </div>
            </div>
          </div>
        </section>

        {/* Philosophy Section */}
        <section className="section-spacing">
          <div className="container px-4 lg:px-8">
            <ScrollReveal>
              <div className="text-center mb-16">
                <h2 className="text-3xl lg:text-5xl font-display font-bold mb-6 bg-gradient-brand bg-clip-text text-transparent">
                  Our Philosophy
                </h2>
                <p className="text-xl text-muted-foreground max-w-3xl mx-auto">
                  Everything we do is guided by three core principles that differentiate us from traditional agencies and consultancies.
                </p>
              </div>
            </ScrollReveal>

            <div className="grid lg:grid-cols-3 gap-8 max-w-6xl mx-auto">
              {philosophyPrinciples.map((principle, index) => (
                <ScrollReveal key={principle.title} delay={index * 150}>
                  <Card className="glass-panel p-8 h-full group hover-lift">
                    <div className="text-center">
                      <div className="w-16 h-16 mx-auto mb-6 rounded-full bg-primary/20 flex items-center justify-center group-hover:scale-110 transition-transform">
                        <span className="text-3xl">{principle.icon}</span>
                      </div>
                      <h3 className="text-xl lg:text-2xl font-bold text-foreground mb-4">
                        {principle.title}
                      </h3>
                      <p className="text-muted-foreground leading-relaxed">
                        {principle.description}
                      </p>
                    </div>
                  </Card>
                </ScrollReveal>
              ))}
            </div>
          </div>
        </section>

        {/* Mission Section */}
        <section className="section-spacing bg-gradient-to-br from-primary/10 to-accent/10">
          <div className="container px-4 lg:px-8">
            <ScrollReveal>
              <div className="max-w-5xl mx-auto text-center">
                <h2 className="text-3xl lg:text-5xl font-display font-bold mb-8 bg-gradient-brand bg-clip-text text-transparent">
                  Our Mission
                </h2>
                
                <div className="glass-panel p-8 lg:p-12 rounded-2xl mb-8">
                  <blockquote className="text-xl lg:text-2xl font-medium text-foreground leading-relaxed italic mb-6">
                    "To transform founder-led companies from content-dependent to content-autonomous by building permanent capabilities that scale with their vision, not external dependencies that limit their growth."
                  </blockquote>
                </div>
                
                <p className="text-lg lg:text-xl text-muted-foreground max-w-4xl mx-auto leading-relaxed">
                  We measure our success not by the content we create, but by the capabilities we transfer. When a founder no longer needs us, we've done our job perfectly.
                </p>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* Why This Matters Now Section */}
        <section className="section-spacing">
          <div className="container px-4 lg:px-8">
            <ScrollReveal>
              <div className="max-w-5xl mx-auto">
                <h2 className="text-3xl lg:text-5xl font-display font-bold mb-12 text-center bg-gradient-brand bg-clip-text text-transparent">
                  Why This Matters Now
                </h2>
                
                <div className="space-y-8 text-lg lg:text-xl text-muted-foreground leading-relaxed">
                  <p>
                    We're entering an era where authentic, founder-led content will be the primary differentiator between companies that scale and those that stagnate. AI is commoditizing generic content, making genuine expertise and authentic voice more valuable than ever.
                  </p>
                  
                  <p className="text-center font-semibold text-primary text-xl lg:text-2xl">
                    The founders who win in the next decade won't be the ones with the biggest marketing budgets— they'll be the ones who built the most efficient, authentic, and scalable content systems.
                  </p>
                  
                  <p>
                    That's exactly what we help you build. Not just content, but the entire capability to create content that converts, scales, and compounds in value over time.
                  </p>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* Final CTA Section */}
        <section className="section-spacing bg-gradient-to-br from-background via-primary/10 to-accent/10">
          <div className="container px-4 lg:px-8">
            <ScrollReveal>
              <div className="max-w-4xl mx-auto text-center">
                <h2 className="text-3xl lg:text-5xl font-display font-bold mb-8 bg-gradient-brand bg-clip-text text-transparent">
                  Ready to Build Your Content Capability?
                </h2>
                
                <p className="text-xl lg:text-2xl text-muted-foreground mb-12 leading-relaxed">
                  Let's discuss how our philosophy and methodology can help you transform from content-dependent to content-autonomous in 90 days.
                </p>
                
                <Link to="/contact">
                  <Button 
                    variant="hero" 
                    size="xl" 
                    className="text-lg lg:text-xl px-12 lg:px-16 py-6 lg:py-8 min-h-[64px] lg:min-h-[72px] animate-glow-pulse shadow-2xl font-black tracking-wide"
                  >
                    <span className="relative z-10">🚀 START THE CONVERSATION</span>
                  </Button>
                </Link>
              </div>
            </ScrollReveal>
          </div>
        </section>
      </main>

      <NewsletterSubscription />
      <Footer />
    </div>
  );
};

export default About;